import * as d3 from 'd3';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';

import _ from 'lodash';
import { twMerge } from 'tailwind-merge';
import { HoldingResponse, SecurityResponse } from '../../../../response/responseSchema';
import { apiClient } from '@app/apiClient';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import stringUtil from '@client/clientUtils/stringUtil';
import TopHoldingStickyLabel from './TopHoldingStickyLabel';
import { GradientText } from '@client/uikit/GradientText';

class ViewStore {
    readonly institutionSlug: string | undefined;
    holdings: HoldingResponse[] = [];
    holdingsByYear: Record<string, HoldingResponse[]> = {};
    reportYears: string[] = [];

    // 포커스된 종목
    focusedSecurity: SecurityResponse | undefined = undefined;

    constructor(institutionSlug: string | undefined) {
        this.institutionSlug = institutionSlug;

        makeAutoObservable(this);
    }

    async fetch() {
        if (!this.institutionSlug) {
            return;
        }

        const holdings = await apiClient.report.public.getHistoricalTopOneHoldings.$query({
            institutionSlug: this.institutionSlug,
        });

        const holdingsByYear = _.groupBy(holdings, 'reportYear');
        const reportYears = _.reverse(_.sortBy(_.keys(holdingsByYear)));

        runInAction(() => {
            this.holdings = holdings;
            this.holdingsByYear = holdingsByYear;
            this.reportYears = reportYears;

            const firstHolding = _.first(this.holdings);
            if (firstHolding) {
                this.setFocusedSecurity(firstHolding.security);
            }
        });
    }

    setFocusedSecurity(security: SecurityResponse | undefined) {
        this.focusedSecurity = security;
    }
}

const WEIGHT_WIDTH = 55;

const HistoricalTopOneHoldings = observer(({ institutionSlug }: { institutionSlug: string }) => {
    const viewStore = useMobxInstance(ViewStore, institutionSlug);

    useEffect(() => {
        viewStore.fetch();
    }, [institutionSlug]);

    const weightScale = useMemo(() => {
        const max = d3.max(viewStore.holdings, (holding) => Number(holding.weight)) || 0;
        return d3.scaleLinear().domain([0, max]).range([0, 100]);
    }, [viewStore.holdings]);

    return (
        <div className="flex flex-col gap-2">
            <TopHoldingStickyLabel
                security={viewStore.focusedSecurity}
                institutionSlug={viewStore.institutionSlug as string}
            />
            {viewStore.reportYears.map((year) => {
                const yearHoldings = viewStore.holdingsByYear[year];

                return (
                    <div key={year} className="flex flex-row justify-between gap-1">
                        <div className="flex flex-col justify-start">
                            <div className="text-base-content/60 flex h-8 shrink-0 items-center text-sm tabular-nums">
                                {year}
                            </div>
                        </div>
                        <div className="flex flex-grow flex-col gap-2 truncate">
                            {_.map(yearHoldings, (holding) => {
                                const isFocused =
                                    holding.security.hash === viewStore.focusedSecurity?.hash;

                                return (
                                    <div
                                        key={holding.hash}
                                        className="flex flex-row gap-2"
                                        onClick={() =>
                                            viewStore.setFocusedSecurity(holding.security)
                                        }
                                    >
                                        <div className="text-base-content/60 flex h-8 shrink-0 items-center text-sm tabular-nums">
                                            {holding.reportQuarter}Q
                                        </div>
                                        <div className="relative flex h-8 w-full items-center justify-between gap-2 truncate">
                                            {/* 바 그래프 */}
                                            <div
                                                className={twMerge(
                                                    'bg-base-300 text-primary/80 absolute top-0 left-0 h-full rounded-xs',
                                                    isFocused &&
                                                        'bg-primary/80 text-primary-content',
                                                )}
                                                style={{
                                                    width: `${weightScale(Number(holding.weight))}%`,
                                                }}
                                            ></div>
                                            <GradientText
                                                gradientShape="hard"
                                                className="absolute top-0 left-0 z-10 flex h-full w-full items-center justify-between truncate text-sm"
                                                stops={
                                                    isFocused
                                                        ? [
                                                              {
                                                                  color: 'var(--color-primary-content)',
                                                              },
                                                              {
                                                                  color: 'color-mix(in oklab, var(--color-base-content) 60%, transparent)',
                                                                  position: `${weightScale(Number(holding.weight))}%`,
                                                              },
                                                              {
                                                                  color: 'var(--color-primary)',
                                                                  position: `calc(100% - ${WEIGHT_WIDTH}px)`,
                                                              },
                                                          ]
                                                        : [
                                                              {
                                                                  color: 'color-mix(in oklab, var(--color-base-content) 60%, transparent)',
                                                              },
                                                          ]
                                                }
                                            >
                                                <div className={twMerge('truncate pl-2 text-sm')}>
                                                    {holding.security.stock?.ticker ||
                                                        holding.security.name}
                                                </div>
                                                <div
                                                    className={twMerge(
                                                        'shrink-0 text-right text-sm tabular-nums',
                                                    )}
                                                    style={{
                                                        width: `${WEIGHT_WIDTH}px`,
                                                    }}
                                                >
                                                    {stringUtil.toPercentage(holding.weight)}
                                                </div>
                                            </GradientText>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
});

export default HistoricalTopOneHoldings;
